"use client";

import React from "react";
import { Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
const minimalInputClasses = "border-0 border-b border-secondary/40 rounded-none px-0 bg-transparent focus-visible:ring-0 focus-visible:border-primary shadow-none text-foreground placeholder:text-muted-foreground/50 md:text-lg h-12 transition-colors";
function ConnectForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="py-16 text-center animate-in fade-in duration-700">
                <h3 className="font-default text-3xl md:text-4xl text-primary mb-4">Consultation Requested</h3>
                <p className="text-muted-foreground text-lg mb-8 max-w-md mx-auto">{successMessage}</p>
                <Button onClick={resetForm} variant="outline" className="border-primary text-primary hover:bg-primary hover:text-primary-foreground h-12 px-8">
                    Submit Another Request
                </Button>
            </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <FormField control={control} name="name" rules={{
        required: "Please enter your full name"
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="sr-only">Full Name</FormLabel>
                            <FormControl>
                                <Input placeholder="Full Name" className={minimalInputClasses} {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="email" rules={{
        required: "Email address is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="sr-only">Email Address</FormLabel>
                            <FormControl>
                                <Input type="email" placeholder="Email Address" className={minimalInputClasses} {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
                            <FormLabel className="sr-only">Phone Number</FormLabel>
                            <FormControl>
                                <Input type="tel" placeholder="Phone Number" className={minimalInputClasses} {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="reason" rules={{
        required: "Please select a practice area"
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="sr-only">Practice Area</FormLabel>
                            <FormControl>
                                <select className={`${minimalInputClasses} w-full appearance-none cursor-pointer`} {...field}>
                                    <option value="" disabled className="bg-background text-muted-foreground">Practice Area</option>
                                    {["Family Law", "Real Estate", "Corporate Litigation", "Estate Planning", "Other"].map((opt, i) => <option key={i} value={opt} className="bg-background text-foreground">{opt}</option>)}
                                </select>
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />
            </div>

            <FormField control={control} name="message" rules={{
      required: "Please provide a brief description of your case"
    }} render={({
      field
    }) => <FormItem>
                        <FormLabel className="sr-only">Case Details</FormLabel>
                        <FormControl>
                            <Textarea placeholder="Briefly describe your legal matter..." className={`${minimalInputClasses} min-h-[120px] resize-none pt-4`} {...field} />
                        </FormControl>
                        <FormMessage />
                    </FormItem>} />

            {submitError && <div className="p-4 border border-destructive/50 bg-destructive/10 text-destructive text-sm">
                    {submitError}
                </div>}

            <Button type="submit" disabled={isSubmitting} className="w-full h-16 bg-primary text-primary-foreground hover:bg-primary/90 text-lg tracking-widest uppercase font-default font-semibold mt-8">
                {isSubmitting ? <>
                        <Loader className="animate-spin mr-3 size-5" />
                        Processing...
                    </> : 'Request Free Consultation'}
            </Button>
        </Form>;
}
export default function RequestAPrivateConsultation() {
  return <section data-section-id="226214" id="request-a-private-consultation" className="bg-background text-foreground relative py-24 md:py-32 overflow-hidden">
            {}
            <div className="absolute inset-0 z-0">
                <div className="absolute inset-0 bg-background/90 z-10" />
                <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687512/img/sterling_law_office_building.png)] bg-cover bg-center blur-sm opacity-40 z-0" />
            </div>

            <div className="relative z-20 container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
                <div className="text-center mb-16">
                    <h2 className="font-default text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-4 text-foreground">
                        Free Consultation
                    </h2>
                    <p className="text-muted-foreground text-lg md:text-xl max-w-2xl mx-auto font-default font-light">
                        Secure your legal standing with Sterling Law Associates. Our partners are ready to review your case with the precision and discretion you deserve.
                    </p>
                </div>

                <div className="bg-background/60 backdrop-blur-md p-8 md:p-12 border-4 border-primary shadow-2xl">
                    <FormProvider formKey="private_consultation_form_home" formId="private_consultation_form_home" sectionName="Request a Private Consultation" defaultValues={{
          name: "",
          email: "",
          phone: "",
          reason: "",
          message: ""
        }} validationRules={{
          name: {
            required: true
          },
          email: {
            required: true,
            pattern: "email"
          },
          reason: {
            required: true
          },
          message: {
            required: true
          }
        }} submitText="Request Free Consultation" successMessage="Your request has been received. A senior associate from Sterling Law will contact you within one business day.">
                        <ConnectForm />
                    </FormProvider>
                </div>
            </div>
        </section>;
}